import React, { FC, useState, useMemo } from 'react';
import {
  formatDistanceToNow,
  format,
  isToday,
  isYesterday,
  isThisWeek,
} from 'date-fns';
import { SalesInboxConversation } from '@dreamai/types';
import noRepliesImg from '../../../icons/no-replies.png';
import { ReactComponent as ConversationBubblesIcon } from '../../../icons/conversation-bubbles.svg';
import { ReactComponent as CampaignIcon } from '../../../icons/campaign.svg';
import { ReactComponent as CloseIcon } from '../../../icons/close.svg';
import OrgAvatar from '../../common/OrgAvatar';
import ErrorState from '../../common/ErrorState';
import { OnboardingModal } from '../../onboarding/OnboardingModal';
import { useOnboardingModals } from '../../../providers';
import './sales-inbox.scss';

interface SalesInboxListProps {
  conversations: SalesInboxConversation[];
  loading: boolean;
  error: string | null;
  onSelectConversation: (conversationId: number) => void;
  onMarkConversationHandled?: (conversationId: number) => void;
}

interface StatusPill {
  text: string;
  variant: string;
  dismissible: boolean;
}

const getStatusPill = (
  conversation: SalesInboxConversation,
): StatusPill | null => {
  // Priority order matches CampaignOverview
  if (conversation.hasUnreadReplies) {
    return { text: '🎉 New reply', variant: 'magic', dismissible: false };
  }
  if (conversation.awaitingOurResponse) {
    return { text: 'Awaiting reply', variant: 'red', dismissible: true };
  }
  if (conversation.mayWantToRespond) {
    return { text: 'May want to respond', variant: 'beige', dismissible: true };
  }
  if (conversation.isNegative) {
    return { text: 'Negative', variant: 'default', dismissible: false };
  }
  return null; // No pill
};

const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);

    if (isToday(date)) {
      return format(date, 'HH:mm');
    }

    if (isYesterday(date)) {
      return 'Yesterday';
    }

    if (isThisWeek(date)) {
      return format(date, 'EEEE');
    }

    return format(date, 'd MMM');
  } catch {
    return dateString;
  }
};

const SalesInboxList: FC<SalesInboxListProps> = ({
  conversations,
  loading,
  error,
  onSelectConversation,
  onMarkConversationHandled,
}) => {
  const { hasSeenModal, markAsSeen } = useOnboardingModals();
  const [showAwaitingReplyModal, setShowAwaitingReplyModal] = useState(false);
  const [showMayWantToRespondModal, setShowMayWantToRespondModal] =
    useState(false);

  // Find first conversation with each tag type
  const firstAwaitingReplyId = useMemo(
    () => conversations.find((c) => c.awaitingOurResponse)?.id,
    [conversations],
  );
  const firstMayWantToRespondId = useMemo(
    () => conversations.find((c) => c.mayWantToRespond)?.id,
    [conversations],
  );

  if (loading) {
    return (
      <div className='sales-inbox-list loading'>
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className='conversation-item skeleton'>
            <div className='skeleton-avatar' />
            <div className='skeleton-content'>
              <div className='skeleton-line short' />
              <div className='skeleton-line long' />
              <div className='skeleton-line medium' />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className='sales-inbox-list error'>
        <ErrorState title='Unable to load conversations' subtitle={error} />
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className='sales-inbox-list empty'>
        <div className='empty-state-wrapper'>
          <img src={noRepliesImg} alt='No replies' className='empty-icon' />
          <h3>No conversations yet</h3>
          <p>When prospects reply to your campaigns, you'll see them here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className='sales-inbox-list'>
      {conversations.map((conversation) => {
        const statusPill = getStatusPill(conversation);

        return (
          <div
            key={`${conversation.id}-${conversation.prospectId}`}
            className={`conversation-item ${conversation.hasUnreadReplies ? 'unread' : ''}`}
            onClick={() => onSelectConversation(conversation.id)}
          >
            <div className='conversation-header'>
              <div className='conversation-left'>
                <div className='thread-count'>
                  <ConversationBubblesIcon />
                  <span>{conversation.threadCount}</span>
                </div>
                {statusPill && (
                  <span className={`status-pill ${statusPill.variant}`}>
                    {/* Throbbing dot for first awaiting reply tag */}
                    {conversation.awaitingOurResponse &&
                      conversation.id === firstAwaitingReplyId &&
                      !hasSeenModal('awaiting_reply') && (
                        <button
                          className='onboarding-dot'
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowAwaitingReplyModal(true);
                          }}
                          aria-label='Learn about awaiting reply'
                        />
                      )}
                    {/* Throbbing dot for first may want to respond tag */}
                    {conversation.mayWantToRespond &&
                      conversation.id === firstMayWantToRespondId &&
                      !hasSeenModal('may_want_to_respond') && (
                        <button
                          className='onboarding-dot'
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowMayWantToRespondModal(true);
                          }}
                          aria-label='Learn about may want to respond'
                        />
                      )}
                    {statusPill.text}
                    {statusPill.dismissible && onMarkConversationHandled && (
                      <button
                        className='pill-dismiss'
                        onClick={(e) => {
                          e.stopPropagation();
                          onMarkConversationHandled(conversation.id);
                        }}
                        aria-label='Dismiss'
                      >
                        <CloseIcon />
                      </button>
                    )}
                  </span>
                )}
              </div>

              <div className='conversation-right'>
                <CampaignIcon />
                <span className='campaign-name'>
                  {conversation.campaignName}
                </span>
              </div>
            </div>

            <div className='conversation-main'>
              <div className='prospect-row'>
                <div className='prospect-info'>
                  <div className='prospect-details'>
                    <span className='prospect-name'>
                      {conversation.prospectName || 'Unknown'}
                    </span>
                    <span className='prospect-email'>
                      {conversation.prospectEmail}
                    </span>
                  </div>
                </div>
                <span className='date'>
                  {formatDate(conversation.lastReplyAt)}
                </span>
              </div>
              <div className='email-preview'>
                <span className='subject'>{conversation.lastReplySubject}</span>
                {conversation.lastReplySnippet && (
                  <span className='snippet'>
                    {conversation.lastReplySnippet}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* Awaiting Reply onboarding modal */}
      <OnboardingModal
        modalKey='awaiting_reply'
        title='Awaiting Reply'
        imageSrc='/onboarding-modals/awaiting-reply.png'
        primaryButtonText='Got it'
        controlled={true}
        show={showAwaitingReplyModal}
        onClose={() => {
          setShowAwaitingReplyModal(false);
          markAsSeen('awaiting_reply');
        }}
      >
        <p>
          This tag means your Sales Manager thinks this prospect needs a reply.
          There may be a draft waiting for your approval, or you may need to
          reply yourself.
        </p>
        <p>
          If no reply is needed, you can dismiss it by clicking the X on the
          tag.
        </p>
      </OnboardingModal>

      {/* May Want to Respond onboarding modal */}
      <OnboardingModal
        modalKey='may_want_to_respond'
        title='May Want to Respond'
        imageSrc='/onboarding-modals/may-want-to-respond.png'
        primaryButtonText='Got it'
        controlled={true}
        show={showMayWantToRespondModal}
        onClose={() => {
          setShowMayWantToRespondModal(false);
          markAsSeen('may_want_to_respond');
        }}
      >
        <p>
          This tag means your Sales Manager thinks you might want to follow up
          with this prospect, even though a reply isn't strictly required.
        </p>
        <p>
          It could be a good opportunity to keep the conversation going. Dismiss
          it if you don't need to respond.
        </p>
      </OnboardingModal>
    </div>
  );
};

export default SalesInboxList;
